import {
  ZadPurchasePaymentSummary,
  ZadSupplierAccountSummary,
  ZadSupplierPayment,
  ZadSupplierPaymentMethod,
  ZadSupplierPaymentStatus,
  ZadSupplierPaymentWritePayload,
} from '../../../models/zad'
import {ZadApiError, resolveZadUserMessage} from './errors'
import {parseZadPriceInput, roundZadMoney3} from './productHelpers'
import {getZadPurchaseSupplierId} from './purchaseHelpers'

export const ZAD_PAYMENT_STATUS_I18N: Record<ZadSupplierPaymentStatus, string> = {
  ACTIVE: 'ZAD.PAYMENTS.STATUS_ACTIVE',
  CANCELLED: 'ZAD.PAYMENTS.STATUS_CANCELLED',
}

export const ZAD_PAYMENT_METHOD_I18N: Record<ZadSupplierPaymentMethod, string> = {
  CASH: 'ZAD.PAYMENTS.METHOD_CASH',
  BANK_TRANSFER: 'ZAD.PAYMENTS.METHOD_BANK',
  CHEQUE: 'ZAD.PAYMENTS.METHOD_CHEQUE',
  CARD: 'ZAD.PAYMENTS.METHOD_CARD',
  OTHER: 'ZAD.PAYMENTS.METHOD_OTHER',
}

export const ZAD_PAYMENT_METHODS: ZadSupplierPaymentMethod[] = [
  'CASH',
  'BANK_TRANSFER',
  'CHEQUE',
  'CARD',
  'OTHER',
]

export type ZadPaymentEditorAllocation = {
  key: string
  purchase: string
  purchaseNumber: string
  purchaseTotal: number
  alreadyAllocated: number
  outstanding: number
  amount: string
  supplierDocumentNumber?: string
  purchaseDate?: string
  netHT?: number
  vatTotal?: number
}

export type ZadPaymentEditorValues = {
  supplier: string
  paymentDate: string
  amount: string
  paymentMethod: ZadSupplierPaymentMethod
  reference: string
  notes: string
  allocations: ZadPaymentEditorAllocation[]
}

export type ZadPaymentPreview = {
  amount: number
  allocatedAmount: number
  unallocatedAmount: number
}

export type ZadSupplierOutstandingKind = 'due' | 'settled' | 'advance'

export function todayZadIsoDate(): string {
  const today = new Date()
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate()
  ).padStart(2, '0')}`
}

export function parseZadPaymentAmount(
  value: string | number | null | undefined
): {ok: true; value: number} | {ok: false; errorId: string} {
  const parsed = parseZadPriceInput(value, {required: true})
  if (!parsed.ok) return {ok: false, errorId: 'ZAD.PAYMENTS.AMOUNT_REQUIRED'}
  if (parsed.value <= 0) return {ok: false, errorId: 'ZAD.PAYMENTS.AMOUNT_INVALID'}
  return {ok: true, value: parsed.value}
}

export function canCancelZadSupplierPayment(status?: ZadSupplierPaymentStatus | string): boolean {
  return status === 'ACTIVE'
}

export function isZadSupplierPaymentActive(status?: ZadSupplierPaymentStatus | string): boolean {
  return status === 'ACTIVE'
}

export function emptyZadPaymentEditorValues(): ZadPaymentEditorValues {
  return {
    supplier: '',
    paymentDate: todayZadIsoDate(),
    amount: '',
    paymentMethod: 'BANK_TRANSFER',
    reference: '',
    notes: '',
    allocations: [],
  }
}

export function previewZadPayment(
  amountValue: string | number,
  allocations: Array<{amount: string | number}>
): ZadPaymentPreview | null {
  const amount = parseZadPaymentAmount(amountValue)
  if (!amount.ok) return null
  let allocated = 0
  for (const row of allocations) {
    const parsed = parseZadPaymentAmount(row.amount)
    if (!parsed.ok) return null
    allocated = roundZadMoney3(allocated + parsed.value)
  }
  return {
    amount: amount.value,
    allocatedAmount: allocated,
    unallocatedAmount: roundZadMoney3(amount.value - allocated),
  }
}

export function isDuplicateZadPaymentPurchase(
  allocations: Array<{purchase: string}>,
  purchaseId: string
): boolean {
  return allocations.some((row) => row.purchase && row.purchase === purchaseId)
}

export function createZadPaymentAllocation(
  summary: ZadPurchasePaymentSummary
): ZadPaymentEditorAllocation {
  const suggested =
    summary.outstanding > 0 ? String(roundZadMoney3(summary.outstanding)) : ''
  return {
    key: `${summary.purchaseId}-${Date.now()}`,
    purchase: summary.purchaseId,
    purchaseNumber: summary.purchaseNumber,
    purchaseTotal: summary.amountPayable ?? summary.total,
    alreadyAllocated: summary.allocated,
    outstanding: summary.outstanding,
    amount: suggested,
    supplierDocumentNumber: summary.supplierDocumentNumber,
    purchaseDate: summary.purchaseDate,
    netHT: summary.netHT,
    vatTotal: summary.vatTotal,
  }
}

export function validateZadPaymentForm(values: ZadPaymentEditorValues): string | null {
  if (!String(values.supplier || '').trim()) return 'ZAD.PAYMENTS.SUPPLIER_REQUIRED'
  if (!String(values.paymentDate || '').trim()) return 'ZAD.PAYMENTS.DATE_REQUIRED'
  const amount = parseZadPaymentAmount(values.amount)
  if (!amount.ok) return amount.errorId
  if (!values.paymentMethod) return 'ZAD.PAYMENTS.METHOD_REQUIRED'
  const seen = new Set<string>()
  let allocated = 0
  for (const row of values.allocations) {
    if (!row.purchase) return 'ZAD.PAYMENTS.PURCHASE_REQUIRED'
    if (seen.has(row.purchase)) return 'ZAD.PAYMENTS.DUPLICATE_PURCHASE'
    seen.add(row.purchase)
    const alloc = parseZadPaymentAmount(row.amount)
    if (!alloc.ok) return alloc.errorId === 'ZAD.PAYMENTS.AMOUNT_INVALID'
      ? 'ZAD.PAYMENTS.ALLOCATION_INVALID'
      : 'ZAD.PAYMENTS.ALLOCATION_REQUIRED'
    if (alloc.value > row.outstanding) return 'ZAD.PAYMENTS.ALLOCATION_EXCEEDS'
    allocated = roundZadMoney3(allocated + alloc.value)
  }
  if (allocated > amount.value) return 'ZAD.PAYMENTS.ALLOCATIONS_EXCEED_PAYMENT'
  return null
}

export function sanitizeZadSupplierPaymentWritePayload(
  payload: ZadSupplierPaymentWritePayload
): ZadSupplierPaymentWritePayload {
  return {
    supplier: String(payload.supplier || '').trim(),
    paymentDate: String(payload.paymentDate || '').trim(),
    amount: Number(payload.amount) || 0,
    paymentMethod: payload.paymentMethod,
    reference: payload.reference != null ? String(payload.reference) : '',
    notes: String(payload.notes || '').trim(),
    allocations: (payload.allocations || []).map((row) => ({
      purchase: String(row.purchase || '').trim(),
      amount: Number(row.amount) || 0,
    })),
  }
}

export function toZadSupplierPaymentPayload(
  values: ZadPaymentEditorValues
): ZadSupplierPaymentWritePayload {
  const amount = parseZadPaymentAmount(values.amount)
  return {
    supplier: String(values.supplier || '').trim(),
    paymentDate: String(values.paymentDate || '').trim(),
    amount: amount.ok ? amount.value : 0,
    paymentMethod: values.paymentMethod,
    reference: String(values.reference ?? ''),
    notes: String(values.notes || '').trim(),
    allocations: values.allocations.map((row) => {
      const alloc = parseZadPaymentAmount(row.amount)
      return {
        purchase: row.purchase,
        amount: alloc.ok ? alloc.value : 0,
      }
    }),
  }
}

export function describeZadSupplierOutstanding(outstanding: number): {
  kind: ZadSupplierOutstandingKind
  amount: number
  i18nId: string
} {
  const value = roundZadMoney3(outstanding)
  if (value === 0) {
    return {kind: 'settled', amount: 0, i18nId: 'ZAD.PAYMENTS.SETTLED'}
  }
  if (value < 0) {
    return {
      kind: 'advance',
      amount: roundZadMoney3(Math.abs(value)),
      i18nId: 'ZAD.PAYMENTS.SUPPLIER_ADVANCE',
    }
  }
  return {kind: 'due', amount: value, i18nId: 'ZAD.PAYMENTS.OUTSTANDING_DUE'}
}

export function getZadPaymentSupplierId(
  supplier: ZadSupplierPayment['supplier'] | string | null | undefined
): string {
  return getZadPurchaseSupplierId(supplier)
}

export function allocationPurchaseId(
  purchase: ZadSupplierPayment['allocations'][number]['purchase']
): string {
  if (!purchase) return ''
  if (typeof purchase === 'string') return purchase
  return purchase._id || ''
}

export function allocationPurchaseNumber(
  allocation: ZadSupplierPayment['allocations'][number]
): string {
  if (allocation.purchaseNumber) return allocation.purchaseNumber
  if (allocation.purchase && typeof allocation.purchase !== 'string') {
    return allocation.purchase.purchaseNumber || '—'
  }
  return '—'
}

export function resolveZadSupplierPaymentMessage(
  err: ZadApiError
): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    VALIDATION_ERROR: 'ZAD.PAYMENTS.VALIDATION_ERROR',
    SUPPLIER_NOT_FOUND: 'ZAD.PURCHASES.SUPPLIER_NOT_FOUND',
    SUPPLIER_INACTIVE: 'ZAD.PURCHASES.SUPPLIER_INACTIVE',
    PURCHASE_NOT_FOUND: 'ZAD.PAYMENTS.PURCHASE_NOT_FOUND',
    PURCHASE_NOT_VALIDATED: 'ZAD.PAYMENTS.PURCHASE_NOT_VALIDATED',
    PURCHASE_SUPPLIER_MISMATCH: 'ZAD.PAYMENTS.PURCHASE_SUPPLIER_MISMATCH',
    DUPLICATE_PURCHASE_ALLOCATION: 'ZAD.PAYMENTS.DUPLICATE_PURCHASE',
    INVALID_PAYMENT_AMOUNT: 'ZAD.PAYMENTS.AMOUNT_INVALID',
    INVALID_ALLOCATION_AMOUNT: 'ZAD.PAYMENTS.ALLOCATION_INVALID',
    PURCHASE_OVERPAYMENT: 'ZAD.PAYMENTS.PURCHASE_OVERPAYMENT',
    PAYMENT_ALREADY_CANCELLED: 'ZAD.PAYMENTS.ALREADY_CANCELLED',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  return resolveZadUserMessage(err)
}

export function supplierSummaryUsesAllActivePayments(
  summary: ZadSupplierAccountSummary
): boolean {
  return typeof summary.paymentsTotal === 'number' && typeof summary.outstanding === 'number'
}
